import { FileAddOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Col, Form, Input, message, Row } from 'antd';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_TIME, MAX_LENGTH_DESCRIPTION } from '../../../../constants/common';
import { TOUR_TRASH_TRANSLATION } from '../../../../constants/define/translation';
import { activeDefault, appStyles } from '../../../../styles';
import { TourTrashForm, TourTrashType } from '../../../../types/tourtrash';
import { CONFIG_CONTAINER_NAME, uploadFileToBlob, UploadFileToBlobInterface } from '../../../../utils/azure';
import { toDateTime } from '../../../../utils/helper';
import { toDataFormEditTourTrashActive, toDataFormTourTrash } from '../../../../utils/transfer/tour-trash';
import { ButtonActionPage } from '../../../atoms';
import { DragDropImageTourTrash } from '../../../molecules/upload';
import { DateTimePickerSection, ProductSection, SettingSection, TourTrashNameSection } from '../form';

interface DetailsTabProps {
  onCreateTourTrash: (values: TourTrashForm, resetFunc: () => void) => void;
  recordEdit?: TourTrashType;
  isHandleViewTourTrash?: boolean;
  isEditTourTrashActive?: boolean;
  onLoading: (isLoad: boolean) => void;
  countrySelected: string;
}

export interface ValidTime {
  validateStatus?: 'success' | 'error';
  help?: string;
  isCheckStartDate?: boolean;
}
const azureCDN = process.env.REACT_APP_AZURE_CDN;
const storageAccountName = process.env.REACT_APP_AZURE_BLOB_ACCOUNT_NAME;

export const DetailsTab = (props: DetailsTabProps) => {
  const { t } = useTranslation(TOUR_TRASH_TRANSLATION);
  const { onCreateTourTrash, recordEdit, isHandleViewTourTrash, isEditTourTrashActive, onLoading, countrySelected } =
    props;

  const [form] = Form.useForm();
  const [startTime, setStartTime] = useState<any>(recordEdit?.startDate || DEFAULT_TIME);
  const [endTime, setEndTime] = useState<any>(recordEdit?.endDate || DEFAULT_TIME);
  const [title, setTitle] = useState<string>('');
  const [loyaltyTier, setLoyaltyTier] = useState<any>(['Par', 'Birdie', 'Eagle']);
  const [isReachedMaxTourTrashDescription, setIsReachedMaxTourTrashDescription] = useState<boolean>(false);
  const [lengthTourTrashDescription, setLengthTourTrashDescription] = useState<number>(0);
  const [isReachedMaxProductDescription, setIsReachedMaxProductDescription] = useState<boolean>(false);
  const [lengthProductDescription, setLengthProductDescription] = useState<number>(0);
  const [beginCheckTourTrashDescription, setBeginCheckTourTrashDescription] = useState<boolean>(false);
  const [beginCheckProductDescription, setBeginCheckProductDescription] = useState<boolean>(false);

  const [imageList, setImageList] = useState<UploadFileToBlobInterface[]>([]);
  const [selectedImage, setSelectedImage] = useState<number>(0);

  const [validTime, setValidTime] = useState<ValidTime>({});
  const [messageErrorUploadImage, setMessageErrorUploadImage] = useState<string>();

  const defaultCountryValues = countrySelected || 'USA';

  const resetFormData = useCallback(() => {
    form.resetFields();
    form.setFieldsValue({
      countries: defaultCountryValues,
    });
    setStartTime(DEFAULT_TIME);
    setEndTime(DEFAULT_TIME);
    setIsReachedMaxTourTrashDescription(false);
    setLengthTourTrashDescription(0);
    setIsReachedMaxProductDescription(false);
    setLengthProductDescription(0);
    setBeginCheckTourTrashDescription(false);
    setBeginCheckProductDescription(false);
    setImageList([]);
    setSelectedImage(0);
    setTitle('');
    setLoyaltyTier(['Par', 'Birdie', 'Eagle']);
    form.setFieldsValue({
      loyaltyTier: ['Par', 'Birdie', 'Eagle'],
      remindPushNotificationFrequency: 1,
    });
  }, []);

  const onFinish = useCallback(
    (values: TourTrashForm) => {
      const dataForm = { ...values };
      setBeginCheckTourTrashDescription(true);
      setBeginCheckProductDescription(true);
      if (dataForm.loyaltyTier && dataForm.loyaltyTier.length > 0) {
        const newOptions = {
          loyalty_tier: loyaltyTier,
        };
        delete dataForm.loyaltyTier;
        dataForm.options = newOptions;
      }
      if (
        !isReachedMaxTourTrashDescription &&
        !isReachedMaxProductDescription &&
        lengthTourTrashDescription !== 0 &&
        lengthProductDescription !== 0
      ) {
        if (isEditTourTrashActive) {
          onCreateTourTrash(dataForm, () => {
            resetFormData();
          });
        } else if (imageList.length === 0) {
          setMessageErrorUploadImage(t('message.error.requireUpload'));
        } else {
          onLoading(true);
          uploadFileToBlob(imageList, CONFIG_CONTAINER_NAME.UPLOADS)
            .then(path => {
              let newImagesCDN = path;
              if (azureCDN) {
                newImagesCDN = path.map(url => url.replace(`${storageAccountName}.blob.core.windows.net`, azureCDN));
              }
              onCreateTourTrash(
                {
                  ...dataForm,
                  ...{
                    productImagePath: newImagesCDN.join(','),
                  },
                },
                () => {
                  resetFormData();
                  onLoading(false);
                },
              );
              setMessageErrorUploadImage(undefined);
            })
            .catch(err => {
              onLoading(false);
              message.error(t('message.error.uploadImage'));
            });
        }
      }
    },
    [
      onCreateTourTrash,
      recordEdit,
      isReachedMaxTourTrashDescription,
      isReachedMaxProductDescription,
      lengthTourTrashDescription,
      lengthProductDescription,
      resetFormData,
      imageList,
      onLoading,
      isEditTourTrashActive,
      loyaltyTier,
    ],
  );

  const onHandleKeyChangeTourTrashDescription = useCallback(() => {
    setBeginCheckTourTrashDescription(true);
    setIsReachedMaxTourTrashDescription(lengthTourTrashDescription > MAX_LENGTH_DESCRIPTION);
  }, [lengthTourTrashDescription]);

  const onHandleChangeTourTrashDescription = useCallback((content: any, delta: any, source: any, editor: any) => {
    setLengthTourTrashDescription(editor.getLength() - 1);
  }, []);

  const onSetTitle = () => {
    setTitle(form.getFieldValue('title'));
  };

  const onHandleKeyChangeProductDescription = useCallback(() => {
    setBeginCheckProductDescription(true);
    setIsReachedMaxProductDescription(lengthProductDescription > MAX_LENGTH_DESCRIPTION);
  }, [lengthProductDescription]);

  const onHandleChangeProductDescription = useCallback((content: any, delta: any, source: any, editor: any) => {
    setLengthProductDescription(editor.getLength() - 1);
  }, []);

  const onHandleSetValidTime = useCallback((newData: ValidTime) => {
    setValidTime(oldValue => ({ ...oldValue, ...newData }));
  }, []);

  const onHandleValidateTime = useCallback(
    (start: moment.Moment | null | any, end: moment.Moment | null): void => {
      const startDate = form.getFieldValue('startDate');
      const endDate = form.getFieldValue('endDate');
      const startDateTime = moment(toDateTime(startDate, start));
      const endDateTime = moment(toDateTime(endDate, end));
      const now = moment();
      let valid: ValidTime = {
        validateStatus: undefined,
        help: undefined,
        isCheckStartDate: undefined,
      };

      if (startDate && endDate && start && end) {
        if (startDateTime.isBefore(now)) {
          valid = {
            validateStatus: 'error',
            help: t('tab.detail.form.validate.startBeforeNow'),
            isCheckStartDate: true,
          };
        } else if (endDateTime.isBefore(now)) {
          valid = {
            validateStatus: 'error',
            help: t('tab.detail.form.validate.endBeforeNow'),
            isCheckStartDate: false,
          };
        } else if (startDateTime.isAfter(endDateTime)) {
          valid = {
            validateStatus: 'error',
            help: t('tab.detail.form.validate.startBeforeEnd'),
            isCheckStartDate: true,
          };
        } else {
          valid = {
            validateStatus: 'success',
            help: undefined,
            isCheckStartDate: true,
          };
        }
      }

      onHandleSetValidTime(valid);
    },
    [onHandleSetValidTime],
  );

  const onChangeStartTime = useCallback(
    (value: moment.Moment | null) => {
      setStartTime(value);
      onHandleValidateTime(value, endTime);
    },
    [endTime, onHandleValidateTime],
  );

  const onChangeEndTime = useCallback(
    (value: moment.Moment | null) => {
      setEndTime(value);
      onHandleValidateTime(startTime, value);
    },
    [startTime, onHandleValidateTime],
  );

  const onHandleChangeDateStart = useCallback(
    (value: any) => {
      setStartTime(value);
      onHandleValidateTime(startTime, endTime);
    },
    [startTime, endTime, onHandleValidateTime],
  );

  const onHandleChangeDateEnd = useCallback(
    value => {
      setEndTime(value);
      onHandleValidateTime(startTime, endTime);
    },
    [startTime, endTime, onHandleValidateTime],
  );

  const onSetImageList = useCallback((data: UploadFileToBlobInterface[]) => {
    setImageList(data);
    setMessageErrorUploadImage(undefined);
  }, []);

  const onSelectedImage = useCallback((index: number) => {
    setSelectedImage(index);
  }, []);

  const onCheckAllChange = useCallback(
    e => {
      const isAllSelected = e.target.checked;
      if (isAllSelected) {
        setLoyaltyTier(['On The Tee', 'Par', 'Birdie', 'Eagle']);
        form.setFieldsValue({ loyaltyTier: ['On The Tee', 'Par', 'Birdie', 'Eagle'] });
      } else {
        setLoyaltyTier([]);
        form.setFieldsValue({ loyaltyTier: [] });
      }
    },
    [form],
  );

  const onChangeCheckbox = useCallback(
    e => {
      setLoyaltyTier(e);
      form.setFieldsValue({ loyaltyTier: e });
    },
    [form],
  );

  useEffect(() => {
    if (recordEdit) {
      if (!isEditTourTrashActive) {
        const dataForm = toDataFormTourTrash(recordEdit);
        form.setFieldsValue(dataForm);
        setStartTime(form.getFieldValue('startTime'));
        setEndTime(form.getFieldValue('endTime'));
      } else {
        const dataForm = toDataFormEditTourTrashActive(recordEdit);
        form.setFieldsValue(dataForm);
      }

      const options = recordEdit?.options ? JSON.parse(recordEdit.options) : null;
      const newLoyaltyTier = options?.loyalty_tier || ['Par', 'Birdie', 'Eagle'];
      setLoyaltyTier(newLoyaltyTier);
      form.setFieldsValue({ loyaltyTier: newLoyaltyTier });

      setImageList(
        recordEdit?.product && recordEdit?.product.images
          ? recordEdit.product.images.map(item => ({ dataURL: item.imageUrl, isImageURL: true }))
          : [],
      );
      setSelectedImage(0);
      setTitle(recordEdit?.title);
    } else {
      resetFormData();
    }
    setMessageErrorUploadImage(undefined);
  }, [recordEdit, resetFormData, isEditTourTrashActive]);

  return (
    <Form layout="vertical" form={form} onFinish={onFinish} name="form-create-tour-trash">
      <Row>
        <StyledSelectProductCol span={9}>
          <DragDropImageTourTrash
            {...{
              isView: !!isHandleViewTourTrash || !!isEditTourTrashActive,
              imageList,
              title,
              startTime,
              endTime,
              onSetImageList,
              messageErrorUploadImage,
              selectedImage,
              onSelectedImage,
              titleSize: '(3x4)',
            }}
            multipleUpload
          />
        </StyledSelectProductCol>
        <StyledFormDetailTourTrash span={15}>
          <StyledColScroll className={isHandleViewTourTrash ? 'disable-form' : 'available-form'}>
            <Form.Item name="tourTrashId" hidden>
              <Input readOnly={isHandleViewTourTrash} />
            </Form.Item>
            <Form.Item name="productId" hidden>
              <Input readOnly={isHandleViewTourTrash} />
            </Form.Item>
            <TourTrashNameSection
              length={lengthTourTrashDescription}
              isReachedMax={isReachedMaxTourTrashDescription}
              onHandleChange={onHandleChangeTourTrashDescription}
              onHandleKeyChange={onHandleKeyChangeTourTrashDescription}
              isHandleViewTourTrash={!!isHandleViewTourTrash}
              beginCheck={beginCheckTourTrashDescription}
              onSetTitle={onSetTitle}
              listTier={loyaltyTier}
              onCheckAllChange={onCheckAllChange}
              onChangeCheckbox={onChangeCheckbox}
            />
            {!isEditTourTrashActive && (
              <DateTimePickerSection
                form={form}
                startTime={startTime}
                endTime={endTime}
                onChangeStartTime={onChangeStartTime}
                onChangeEndTime={onChangeEndTime}
                onHandleChangeDateStart={onHandleChangeDateStart}
                onHandleChangeDateEnd={onHandleChangeDateEnd}
                validTime={validTime}
                isHandleViewTourTrash={!!isHandleViewTourTrash}
              />
            )}
            <ProductSection
              length={lengthProductDescription}
              isReachedMax={isReachedMaxProductDescription}
              onHandleChange={onHandleChangeProductDescription}
              onHandleKeyChange={onHandleKeyChangeProductDescription}
              isHandleViewTourTrash={!!isHandleViewTourTrash}
              isEditTourTrashActive={!!isEditTourTrashActive}
              beginCheck={beginCheckProductDescription}
            />
            {!isEditTourTrashActive && (
              <div style={{ display: 'none' }}>
                <SettingSection isHandleViewTourTrash={!!isHandleViewTourTrash} />
              </div>
            )}
          </StyledColScroll>
          {!isHandleViewTourTrash && (
            <ButtonActionPage
              text={recordEdit ? t('tab.detail.update') : t('tab.detail.create')}
              id="1"
              className="btn-submit-form-tour-trash"
              htmlType="submit"
              icon={!recordEdit ? <FileAddOutlined /> : undefined}
              onClick={() => {
                setBeginCheckTourTrashDescription(true);
                setBeginCheckProductDescription(true);
              }}
            />
          )}
        </StyledFormDetailTourTrash>
      </Row>
    </Form>
  );
};

const StyledSelectProductCol = styled(Col)`
  min-height: 400px;
  height: 500px;
  background: transparent linear-gradient(180deg, #dedede00 0%, #7e7e7e 100%) 0% 0% no-repeat padding-box;
  position: relative;
`;

const StyledFormDetailTourTrash = styled(Col)`
  & .ant-picker {
    width: 100%;
    padding: 0;
    ${appStyles.commonBorderRadius}
    & input {
      padding: 4px 0 4px 12px;
    }
    & .ant-picker-suffix {
      font-size: 20px;
      ${appStyles.background.black}
      ${appStyles.color.white}
      padding: 8px;
    }
  }

  & button.btn-submit-form-tour-trash {
    ${appStyles.commonButtonSubmitted}
  }

  & input[type='text'],
  & input[type='number'] {
    ${appStyles.inputStyle}
  }

  & textarea {
    ${appStyles.commonBorderRadius}
  }

  & .ant-input-affix-wrapper {
    padding: 0 11px;
    ${appStyles.commonBorderRadius}
  }

  & .ant-form-item {
    ${appStyles.commonItemMarginBottom}
  }

  & .ant-form-item-explain {
    margin: 0;
  }

  & .ant-form-item-required::before {
    display: none !important;
  }

  & .ant-picker-clear {
    right: 40px;
  }

  & .ant-switch-checked {
    ${activeDefault}
  }

  & .ant-descriptions {
    cursor: default;
    & .ant-descriptions-header {
      margin-bottom: 5px;
    }
  }

  & .row-datetime-picker .ant-form-item-explain-success > div[role='alert'] {
    ${appStyles.color.error}
  }

  & .ant-form-item-explain-error > div[role='alert'] {
    width: 200%;
    ${appStyles.color.error}
  }

  & .form-description .ant-form-item-explain-success > div[role='alert'] {
    width: 200%;
    ${appStyles.color.black}
  }

  & .form-description div[role='alert'] {
    font-size: 12px;
  }

  .right-5 {
    margin-right: 10px;
    margin-bottom: 1px;
  }

  .anticon-question-circle,
  .ant-picker-suffix {
    cursor: pointer;
    pointer-events: auto;
  }
`;

const StyledColScroll = styled(Col)`
  overflow-y: scroll;
  overflow-x: hidden;
  min-height: 350px;
  height: 420px;
  padding: 0 0.5rem 0 1.5rem;
`;
